import { useState } from 'react';
import { AlertTriangle, Key, Mail, Save, Send, Trash2, User, X } from 'lucide-react';
import { Navigate, useNavigate } from 'react-router-dom';
import DashboardLayout from './DashboardLayout';
import { useUser } from '../../contexts/UserContext';
import { deleteAccount, updateUserSettings } from '../../services/api';
import { useTranslation } from 'react-i18next';

const SettingsPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { userInfo, refetchUserInfo } = useUser();
  const [username, setUsername] = useState(userInfo?.name || '');
  const [email, setEmail] = useState(userInfo?.addr || '');
  const [pin, setPin] = useState(userInfo?.pin || '');
  const [sendPin, setSendPin] = useState(userInfo?.send_pin || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [redirectToDashboard, setRedirectToDashboard] = useState(false);
  const [isPlanModalOpen, setIsPlanModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const validateEmail = (email: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const validateUsername = (username: string) => {
    return /^[a-zA-Z0-9_-]{3,20}$/.test(username);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    if (!username.trim() || !validateUsername(username)) {
      setError(t('settings.validation.username'));
      setLoading(false);
      return;
    }

    if (!email.trim() || !validateEmail(email)) {
      setError(t('settings.validation.email'));
      setLoading(false);
      return;
    }

    try {
      const response = await updateUserSettings({ 
        name: username,
        real: email,
        pin,
        send_pin: sendPin
      });
      
      if (response.code === 0) {
        setSuccess(t('settings.success'));
        await refetchUserInfo();
        if (username && email) {
          setTimeout(() => setRedirectToDashboard(true), 1500);
        }
      } else {
        setError(response.message || t('common.error'));
      }
    } catch (err) {
      setError(t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    setDeleteLoading(true);
    setDeleteError(null);
    try {
      const response = await deleteAccount(deleteConfirm);
      if (response.code === 0) {
        await refetchUserInfo();
        navigate('/');
      } else {
        setDeleteError(response.err || response.message || t('settings.deleteAccount.error'));
      }
    } catch (err) {
      setDeleteError(t('settings.deleteAccount.error'));
    } finally {
      setDeleteLoading(false);
    }
  };

  if (redirectToDashboard) {
    return <Navigate to="/dashboard" replace />;
  }

  const isIncomplete = !userInfo?.name || !userInfo?.addr;

  return (
    <DashboardLayout
      isPlanModalOpen={isPlanModalOpen}
      onPlanModalClose={() => setIsPlanModalOpen(false)}
      onOpenPlanModal={() => setIsPlanModalOpen(true)}
    >
      <div className="max-w-2xl mx-auto space-y-6">
        <div className="bg-white rounded-xl shadow-sm">
          <div className="p-6 border-b border-gray-200">
            <h1 className="text-2xl font-semibold text-gray-900">
              {t('settings.title')}
            </h1>
            {isIncomplete && (
              <div className="mt-2 text-sm text-red-600 bg-red-50 p-3 rounded-md">
                {t('settings.completeProfile')}
              </div>
            )}
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                {error}
              </div>
            )}
            
            {success && (
              <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg">
                {success}
              </div>
            )}

            <div>
              <label htmlFor="username" className="block text-sm font-medium text-gray-700">
                {t('settings.form.username.label')}
              </label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <User className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  id="username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder={t('settings.form.username.placeholder')}
                  required
                />
              </div>
              <p className="mt-2 text-sm text-gray-500">
                {t('settings.form.username.help')}
              </p>
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                {t('settings.form.email.label')}
              </label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder={t('settings.form.email.placeholder')}
                  required
                />
              </div>
              <p className="mt-2 text-sm text-gray-500">
                {t('settings.form.email.help')}
              </p>
            </div>

            <div>
              <label htmlFor="pin" className="block text-sm font-medium text-gray-700">
                {t('settings.form.pin.label')}
              </label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Key className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  id="pin"
                  value={pin}
                  onChange={(e) => setPin(e.target.value)}
                  pattern="[A-Za-z0-9]{2,6}"
                  maxLength={6}
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder={t('settings.form.pin.placeholder')}
                />
              </div>
              <p className="mt-2 text-sm text-gray-500">
                {t('settings.form.pin.help')}
              </p>
            </div>

            <div>
              <label htmlFor="sendPin" className="block text-sm font-medium text-gray-700">
                {t('settings.form.sendPin.label')}
              </label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Send className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  id="sendPin"
                  value={sendPin}
                  onChange={(e) => setSendPin(e.target.value)}
                  pattern="[A-Za-z0-9]{2,6}"
                  maxLength={6}
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder={t('settings.form.sendPin.placeholder')}
                />
              </div>
              <p className="mt-2 text-sm text-gray-500">
                {t('settings.form.sendPin.help')}
              </p>
            </div>

            <div className="pt-4">
              <button
                type="submit"
                disabled={loading}
                className="w-full flex justify-center items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    {t('common.save')}
                  </>
                )}
              </button>
            </div>
          </form>
        </div>

        <section className="bg-white rounded-xl shadow-sm border border-red-100">
          <div className="p-6 border-b border-red-100">
            <div className="flex items-start gap-3">
              <div className="mt-0.5 flex h-9 w-9 items-center justify-center rounded-full bg-red-50 text-red-600">
                <AlertTriangle className="h-5 w-5" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  {t('settings.deleteAccount.title')}
                </h2>
                <p className="mt-1 text-sm text-gray-600">
                  {t('settings.deleteAccount.description')}
                </p>
              </div>
            </div>
          </div>
          <div className="p-6">
            <button
              type="button"
              onClick={() => {
                setDeleteConfirm('');
                setDeleteError(null);
                setIsDeleteModalOpen(true);
              }}
              className="inline-flex items-center px-4 py-2 border border-red-200 rounded-md text-sm font-medium text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              {t('settings.deleteAccount.open')}
            </button>
          </div>
        </section>

        {isDeleteModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/50 px-4">
            <div className="w-full max-w-md rounded-xl bg-white shadow-xl">
              <div className="flex items-start justify-between border-b border-gray-200 p-5">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    {t('settings.deleteAccount.modalTitle')}
                  </h3>
                  <p className="mt-1 text-sm text-gray-600">
                    {t('settings.deleteAccount.modalDescription')}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setIsDeleteModalOpen(false)}
                  className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                  aria-label={t('common.close', 'Close')}
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="space-y-4 p-5">
                {deleteError && (
                  <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                    {deleteError}
                  </div>
                )}
                <label htmlFor="deleteConfirm" className="block text-sm font-medium text-gray-700">
                  {t('settings.deleteAccount.confirmLabel')}
                </label>
                <input
                  id="deleteConfirm"
                  type="text"
                  value={deleteConfirm}
                  onChange={(event) => setDeleteConfirm(event.target.value)}
                  className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-red-500 focus:outline-none focus:ring-2 focus:ring-red-500"
                  placeholder="DELETE"
                />
                <div className="flex justify-end gap-3 pt-2">
                  <button
                    type="button"
                    onClick={() => setIsDeleteModalOpen(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900"
                  >
                    {t('common.cancel')}
                  </button>
                  <button
                    type="button"
                    onClick={handleDeleteAccount}
                    disabled={deleteConfirm !== 'DELETE' || deleteLoading}
                    className="inline-flex items-center rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {deleteLoading ? (
                      <div className="h-4 w-4 rounded-full border-2 border-white border-t-transparent animate-spin" />
                    ) : (
                      <>
                        <Trash2 className="mr-2 h-4 w-4" />
                        {t('settings.deleteAccount.confirm')}
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default SettingsPage;
